import { useEffect, useState } from "react";
import { api, apiError } from "../lib/api";
import { toast } from "sonner";
import { Wallet, Check, X, Plus, IndianRupee } from "lucide-react";
import { useAuth } from "../context/AuthContext";

/**
 * Loans — request, endorse (FH), approve (Owner/Head), record repayments.
 * Caps (Owner-configurable): max 1 active loan per person + max amount.
 */
export default function LoansPage() {
    const { user } = useAuth();
    const eff = user?.effective_role || user?.role;
    const isFH = eff === "FACILITY_HEAD";
    const isOwnerHead = eff === "OWNER" || eff === "HEAD";

    const [rows, setRows] = useState([]);
    const [statusFilter, setStatusFilter] = useState("");
    const [showNew, setShowNew] = useState(false);
    const [sites, setSites] = useState([]);
    const [staff, setStaff] = useState([]);
    const [form, setForm] = useState({ staff_id: "", site_id: "", amount: "", reason: "", repayment_months: 6 });
    const [busy, setBusy] = useState(false);
    const [settings, setSettings] = useState({ max_amount_inr: 25000, max_active_per_person: 1 });
    const [expanded, setExpanded] = useState({});
    const [repayments, setRepayments] = useState({});
    const [repayInput, setRepayInput] = useState({});

    async function load() {
        try {
            const q = statusFilter ? `?status=${statusFilter}` : "";
            const r = await api.get(`/hr/loans${q}`);
            setRows(r.data || []);
        } catch (e) { toast.error(apiError(e)); }
    }
    async function loadSettings() {
        try {
            const r = await api.get("/hr/loan-settings");
            setSettings(r.data || settings);
        } catch { /* ignore */ }
    }
    async function loadRefs() {
        try {
            const s = await api.get("/sites"); setSites(s.data || []);
        } catch { /* ignore */ }
    }
    async function loadStaffFor(siteId) {
        if (!siteId) { setStaff([]); return; }
        try {
            const r = await api.get(`/sites/${siteId}/roster`);
            setStaff((r.data && r.data.rows) || r.data || []);
        } catch { setStaff([]); }
    }
    useEffect(() => { load(); }, [statusFilter]);
    useEffect(() => { loadRefs(); loadSettings(); }, []);
    useEffect(() => { loadStaffFor(form.site_id); }, [form.site_id]);

    async function create() {
        setBusy(true);
        try {
            await api.post("/hr/loans", { ...form, amount: Number(form.amount), repayment_months: Number(form.repayment_months) });
            toast.success("Loan request raised.");
            setShowNew(false);
            setForm({ staff_id: "", site_id: "", amount: "", reason: "", repayment_months: 6 });
            load();
        } catch (e) { toast.error(apiError(e)); }
        finally { setBusy(false); }
    }

    async function endorse(id) {
        const note = window.prompt("Endorsement note (optional):", "");
        if (note === null) return;
        setBusy(true);
        try {
            await api.post(`/hr/loans/${id}/endorse`, { endorsement_note: note });
            toast.success("Endorsed.");
            load();
        } catch (e) { toast.error(apiError(e)); }
        finally { setBusy(false); }
    }

    async function decide(id, decision) {
        let approved_amount = null;
        if (decision === "APPROVED") {
            const cur = rows.find((r) => r.id === id);
            const proposed = window.prompt(`Approved amount (₹). Requested ₹${cur?.amount}:`, String(cur?.amount || 0));
            if (proposed === null) return;
            approved_amount = Number(proposed);
        }
        const reason = decision === "REJECTED" ? window.prompt("Reason?") || "" : "";
        setBusy(true);
        try {
            await api.post(`/hr/loans/${id}/decide`, { decision, approved_amount, reason });
            toast.success(`Loan ${decision.toLowerCase()}.`);
            load();
        } catch (e) { toast.error(apiError(e)); }
        finally { setBusy(false); }
    }

    async function toggleExpand(loan) {
        const next = !expanded[loan.id];
        setExpanded((s) => ({ ...s, [loan.id]: next }));
        if (next && !repayments[loan.id]) {
            try {
                const r = await api.get(`/hr/loans/${loan.id}/repayments`);
                setRepayments((s) => ({ ...s, [loan.id]: r.data || [] }));
            } catch { /* ignore */ }
        }
    }

    async function addRepayment(loan) {
        const amt = Number(repayInput[loan.id] || 0);
        if (!amt || amt <= 0) { toast.error("Enter a positive amount."); return; }
        setBusy(true);
        try {
            const r = await api.post(`/hr/loans/${loan.id}/repay`, { amount: amt });
            setRepayments((s) => ({ ...s, [loan.id]: [r.data.repayment, ...(s[loan.id] || [])] }));
            setRepayInput((s) => ({ ...s, [loan.id]: "" }));
            toast.success(`Recorded ₹${amt}. Outstanding ₹${r.data.outstanding}.`);
            load();
        } catch (e) { toast.error(apiError(e)); }
        finally { setBusy(false); }
    }

    async function updateSettings() {
        const maxA = window.prompt(`Max loan amount (₹). Currently ₹${settings.max_amount_inr}:`, settings.max_amount_inr);
        if (maxA === null) return;
        setBusy(true);
        try {
            await api.put("/hr/loan-settings", { max_amount_inr: Number(maxA), max_active_per_person: 1 });
            toast.success("Settings updated.");
            loadSettings();
        } catch (e) { toast.error(apiError(e)); }
        finally { setBusy(false); }
    }

    return (
        <div className="mx-auto max-w-6xl px-4 sm:px-6 py-6" data-testid="loans-page">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <div>
                    <h2 className="text-xl font-semibold text-[#1E3355] flex items-center gap-2">
                        <Wallet className="w-5 h-5" /> Loans &amp; Advances
                    </h2>
                    <p className="text-xs text-gray-600">
                        Cap: max <b>{settings.max_active_per_person}</b> active per person · max ₹{Number(settings.max_amount_inr || 0).toLocaleString("en-IN")}
                        {isOwnerHead && <button onClick={updateSettings} className="ml-2 text-xs text-[#1E3355] underline" data-testid="loans-settings-btn">Edit cap</button>}
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="text-sm border border-gray-300 rounded-md px-2 py-1"
                        data-testid="loan-status-filter"
                    >
                        <option value="">All statuses</option>
                        <option value="PENDING">Pending</option>
                        <option value="ENDORSED">Endorsed</option>
                        <option value="APPROVED">Approved</option>
                        <option value="REJECTED">Rejected</option>
                        <option value="SETTLED">Settled</option>
                    </select>
                    <button
                        onClick={() => setShowNew(true)}
                        className="inline-flex items-center gap-1 rounded-full bg-[#E02B2B] text-white text-sm px-3 py-1.5 hover:bg-red-700"
                        data-testid="loan-new-btn"
                    >
                        <Plus className="w-4 h-4" /> New request
                    </button>
                </div>
            </div>

            {showNew && (
                <div className="rounded-xl border border-gray-200 bg-white p-4 mb-4" data-testid="loan-new-form">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        <div>
                            <label className="text-xs text-gray-600">Site</label>
                            <select value={form.site_id} onChange={(e) => setForm({ ...form, site_id: e.target.value, staff_id: "" })}
                                className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm" data-testid="loan-site">
                                <option value="">Select site</option>
                                {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="text-xs text-gray-600">Staff</label>
                            <select value={form.staff_id} onChange={(e) => setForm({ ...form, staff_id: e.target.value })}
                                className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm" data-testid="loan-staff">
                                <option value="">Select staff</option>
                                {staff.map((s) => <option key={s.id} value={s.id}>{s.name} · {s.role}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="text-xs text-gray-600">Amount (₹)</label>
                            <input type="number" inputMode="numeric" value={form.amount}
                                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                                className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm" data-testid="loan-amount" />
                        </div>
                        <div>
                            <label className="text-xs text-gray-600">Repayment months</label>
                            <input type="number" inputMode="numeric" value={form.repayment_months}
                                onChange={(e) => setForm({ ...form, repayment_months: e.target.value })}
                                className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm" data-testid="loan-months" />
                        </div>
                        <div className="sm:col-span-2 lg:col-span-3">
                            <label className="text-xs text-gray-600">Reason</label>
                            <input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })}
                                className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm" data-testid="loan-reason" />
                        </div>
                    </div>
                    <div className="flex items-center gap-2 mt-3">
                        <button onClick={create} disabled={busy || !form.staff_id || !form.site_id || !form.amount}
                            className="inline-flex items-center gap-1 rounded-full bg-[#1E3355] text-white text-sm px-3 py-1.5 disabled:opacity-50" data-testid="loan-submit">
                            Submit
                        </button>
                        <button onClick={() => setShowNew(false)} className="text-sm text-gray-600 px-3 py-1.5" data-testid="loan-cancel">Cancel</button>
                    </div>
                </div>
            )}

            <div className="fx-table-scroll bg-white rounded-xl border border-gray-200">
                <table className="w-full text-sm">
                    <thead className="text-left text-xs uppercase tracking-wider text-gray-600 border-b border-gray-200">
                        <tr>
                            <th className="p-3">Staff</th>
                            <th className="p-3">Site</th>
                            <th className="p-3">Amount</th>
                            <th className="p-3">Approved</th>
                            <th className="p-3">Outstanding</th>
                            <th className="p-3">Status</th>
                            <th className="p-3">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length === 0 && (
                            <tr><td colSpan="7" className="p-6 text-center text-xs text-gray-500" data-testid="loan-empty">No loan requests.</td></tr>
                        )}
                        {rows.map((r) => (
                            <>
                                <tr key={r.id} className="border-b border-gray-100" data-testid={`loan-row-${r.id}`}>
                                    <td className="p-3">
                                        <button onClick={() => toggleExpand(r)} className="text-left text-[#1E3355] hover:underline" data-testid={`loan-expand-${r.id}`}>
                                            {r.staff_id}
                                        </button>
                                    </td>
                                    <td className="p-3">{r.site_id}</td>
                                    <td className="p-3 inline-flex items-center gap-0.5"><IndianRupee className="w-3 h-3" />{r.amount?.toLocaleString("en-IN")}</td>
                                    <td className="p-3">{r.approved_amount ? `₹${r.approved_amount.toLocaleString("en-IN")}` : "—"}</td>
                                    <td className="p-3">{r.outstanding ? `₹${r.outstanding.toLocaleString("en-IN")}` : "—"}</td>
                                    <td className="p-3">
                                        <span className={`text-xs px-2 py-0.5 rounded-full ${r.status === "APPROVED" ? "bg-emerald-100 text-emerald-700" : r.status === "REJECTED" ? "bg-red-100 text-red-700" : r.status === "SETTLED" ? "bg-blue-100 text-blue-700" : r.status === "ENDORSED" ? "bg-indigo-100 text-indigo-700" : "bg-amber-100 text-amber-800"}`}>
                                            {r.status}
                                        </span>
                                    </td>
                                    <td className="p-3">
                                        <div className="flex items-center gap-1 flex-wrap">
                                            {r.status === "PENDING" && (isFH || isOwnerHead) && (
                                                <button onClick={() => endorse(r.id)} className="text-xs px-2 py-0.5 rounded-full bg-indigo-600 text-white hover:bg-indigo-700" data-testid={`loan-endorse-${r.id}`}>Endorse</button>
                                            )}
                                            {(r.status === "PENDING" || r.status === "ENDORSED") && isOwnerHead && (
                                                <>
                                                    <button onClick={() => decide(r.id, "APPROVED")} className="text-xs px-2 py-0.5 rounded-full bg-emerald-600 text-white hover:bg-emerald-700 inline-flex items-center gap-1" data-testid={`loan-approve-${r.id}`}>
                                                        <Check className="w-3 h-3" /> Approve
                                                    </button>
                                                    <button onClick={() => decide(r.id, "REJECTED")} className="text-xs px-2 py-0.5 rounded-full bg-red-600 text-white hover:bg-red-700 inline-flex items-center gap-1" data-testid={`loan-reject-${r.id}`}>
                                                        <X className="w-3 h-3" /> Reject
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                                {expanded[r.id] && (
                                    <tr className="bg-gray-50 border-b border-gray-200" data-testid={`loan-detail-${r.id}`}>
                                        <td colSpan="7" className="p-3">
                                            <div className="text-xs text-gray-700">
                                                <div><b>Reason:</b> {r.reason || "—"}</div>
                                                <div><b>Requested by:</b> {r.requester_name || r.requester_id} ({r.requester_role})</div>
                                                {r.endorsement_note && <div><b>Endorsement:</b> {r.endorsement_note}</div>}
                                                {r.decision_reason && <div><b>Decision reason:</b> {r.decision_reason}</div>}
                                            </div>
                                            {r.status === "APPROVED" && r.outstanding > 0 && isOwnerHead && (
                                                <div className="mt-3 flex items-center gap-2">
                                                    <input
                                                        type="number" inputMode="numeric" placeholder="Repayment amount"
                                                        value={repayInput[r.id] || ""}
                                                        onChange={(e) => setRepayInput((s) => ({ ...s, [r.id]: e.target.value }))}
                                                        className="border border-gray-300 rounded-md px-2 py-1 text-sm w-32"
                                                        data-testid={`loan-repay-input-${r.id}`}
                                                    />
                                                    <button onClick={() => addRepayment(r)} className="text-xs px-2 py-1 rounded-full bg-[#1E3355] text-white hover:bg-[#15243C]" data-testid={`loan-repay-submit-${r.id}`}>
                                                        Add repayment
                                                    </button>
                                                </div>
                                            )}
                                            {(repayments[r.id] || []).length > 0 && (
                                                <div className="mt-2 text-xs">
                                                    <div className="font-medium text-gray-700 mb-1">Repayment log</div>
                                                    <ul className="space-y-0.5">
                                                        {repayments[r.id].map((p) => (
                                                            <li key={p.id} className="text-gray-700" data-testid={`loan-repay-item-${p.id}`}>
                                                                {p.repaid_on} · ₹{p.amount.toLocaleString("en-IN")} · {p.note || ""}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                )}
                            </>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
